import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Returns `true` once it has fully handled the Escape key (e.g. cancelled an in-progress edit). */
export type EscapeInterceptor = () => boolean;

type RegisterEscapeInterceptor = (interceptor: EscapeInterceptor) => () => void;

export interface SideDrawerEscapeProviderProps {
  children: React.ReactNode;
  registerEscapeInterceptor: RegisterEscapeInterceptor;
}

export interface SideDrawerEscapeInterceptorRef {
  /** Consulted by `SideDrawer`'s own `onEscapeKeyDown` — calls the currently-registered interceptor, if any. */
  consumeEscapeInterceptor: () => boolean;
  registerEscapeInterceptor: RegisterEscapeInterceptor;
}

// ─── Context ──────────────────────────────────────────────────────────────────

// Defaults to a no-op so descendants (e.g. `ServiceTimeField`) work standalone, outside any `SideDrawer`.
const noopRegister: RegisterEscapeInterceptor = () => () => {};
const SideDrawerEscapeContext = createContext<RegisterEscapeInterceptor>(noopRegister);

// ─── Provider (internal — wired by `SideDrawer` itself) ───────────────────────

export function SideDrawerEscapeProvider({
  children,
  registerEscapeInterceptor,
}: SideDrawerEscapeProviderProps): React.JSX.Element {
  return (
    <SideDrawerEscapeContext.Provider value={registerEscapeInterceptor}>{children}</SideDrawerEscapeContext.Provider>
  );
}

/**
 * Holds the stack of currently-claimed Escape interceptors. Used once, by `SideDrawer`, to back its
 * provider + its own `onEscapeKeyDown` handler. A stack (not a single slot) is required because two
 * fields can be mid-edit at once: registering pushes an entry, unregistering removes that specific
 * entry (wherever it sits), and Escape always consults the top — so confirming the most-recently
 * registered field (removing its entry) correctly falls back to the still-active one underneath,
 * instead of leaving no interceptor registered at all.
 */
export function useSideDrawerEscapeInterceptorRef(): SideDrawerEscapeInterceptorRef {
  const interceptorStackRef = useRef<EscapeInterceptor[]>([]);

  const registerEscapeInterceptor = useCallback<RegisterEscapeInterceptor>((interceptor) => {
    interceptorStackRef.current.push(interceptor);
    return (): void => {
      interceptorStackRef.current = interceptorStackRef.current.filter((candidate) => candidate !== interceptor);
    };
  }, []);

  const consumeEscapeInterceptor = useCallback((): boolean => {
    const stack = interceptorStackRef.current;
    return stack[stack.length - 1]?.() ?? false;
  }, []);

  return { consumeEscapeInterceptor, registerEscapeInterceptor };
}

// ─── Consumer hook (public — used by descendants such as `ServiceTimeField`) ──

/**
 * Claims the next Escape keydown while `active` is true, so the nearest ancestor `SideDrawer`
 * (if any) skips its own close/close-confirm handling for it. Registers on activation and
 * unregisters on deactivation or unmount. Outside a `SideDrawer`, registration is a safe no-op.
 */
export function useSideDrawerEscapeInterceptor(active: boolean, interceptor: EscapeInterceptor): void {
  const registerEscapeInterceptor = useContext(SideDrawerEscapeContext);
  const interceptorRef = useRef(interceptor);
  interceptorRef.current = interceptor;

  useEffect(() => {
    if (!active) return undefined;
    return registerEscapeInterceptor((): boolean => interceptorRef.current());
  }, [active, registerEscapeInterceptor]);
}
